import React, { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import { showModal, setPaymentRouteType } from 'ducks/trialWarning'
import { getApp, getUpgraded, updateApp } from 'ducks/apps'
import { getDomains } from 'ducks/domains'
import { getOrganization } from 'ducks/organizations'

import Icon from 'components/Shared/Icon'
import Button from 'components/Shared/Button'
import { GroupedAccordion } from 'components/Shared/Accordion'
import EmptyState from 'components/Shared/EmptyState'
import Loading from 'components/Shared/Loading'
import ToggleButton from 'components/Shared/Forms/ToggleButton'
import CalloutCard from 'components/Shared/CalloutCard'

import { useFlags } from 'flags'

import WebPublish from './Web'
import AndroidPublish from './Android/AndroidPublish'
import IOSPublish from './IOS/IOSPublish'
import PWAPublish from './PWA'

import './Publish.scss'

const PublishPanel = () => {
  const dispatch = useDispatch()

  // Published Apps feature flag
  const { hasPublishedApps } = useFlags()

  const { appId } = useParams()

  const app = useSelector(state => getApp(state, appId))

  const organization = useSelector(state =>
    getOrganization(state, app?.OrganizationId)
  )

  const disabled = hasPublishedApps && !app?.published

  const handleTogglePublished = () => {
    dispatch(updateApp(appId, { published: !app.published }))
  }

  const handleUpgradeModal = () => {
    dispatch(setPaymentRouteType('upgrade'))
    dispatch(showModal())
  }

  if (!app) {
    return (
      <EmptyState>
        <Loading large />
      </EmptyState>
    )
  }

  return (
    <div className="publish-panel">
      <div className="left-panel-header">
        <div className="left-panel-header-title">
          <Icon type="done-all" color="green" />
          <h2>Publish</h2>
          {hasPublishedApps && app.primaryPlatform !== 'web' ? (
            <ToggleButton
              value={app?.published}
              onChange={handleTogglePublished}
              disabled={!organization.active}
            />
          ) : null}
        </div>
        {hasPublishedApps && app.primaryPlatform !== 'web' ? (
          <div className="left-panel-header-subtext">
            <span>
              Any apps that share this database will also be marked as
              published.
            </span>
          </div>
        ) : null}
      </div>
      <div>
        {app.primaryPlatform === 'web' ? (
          <Publishing type="web" />
        ) : (
          <>
            {hasPublishedApps && !organization.active ? (
              <CalloutCard
                color="grey"
                title="Upgrade to Publish Your Apps!"
                body="Switch to a paid plan to start sharing your apps with the world."
                button={{
                  green: true,
                  small: true,
                  children: 'Upgrade',
                  onClick: handleUpgradeModal,
                }}
                alignment="left"
              />
            ) : null}
            <GroupedAccordion
              group="publish-accordions"
              itemId="publish-android"
              title="Android App"
              className="library-inspect-accordion"
              renderChildren={() => <Publishing type="android" />}
              disabled={disabled}
            />
            <GroupedAccordion
              group="publish-accordions"
              itemId="publish-ios"
              title="iOS App"
              className="library-inspect-accordion"
              renderChildren={() => <Publishing type="ios" />}
              disabled={disabled}
            />
            <GroupedAccordion
              group="publish-accordions"
              itemId="publish-pwa"
              title="Progressive Web App"
              className="library-inspect-accordion"
              renderChildren={() => <Publishing type="pwa" />}
              disabled={disabled}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default PublishPanel

const Upgrade = () => {
  const dispatch = useDispatch()

  const handleUpgradeModal = () => {
    dispatch(setPaymentRouteType('upgrade'))
    dispatch(showModal())
  }

  return (
    <div>
      <p className="publish-lead-message">
        In order to publish your app, you must upgrade to a paid plan.
      </p>
      <div className="publish-submit">
        <div className="publish-submit-sub">
          <Button green onClick={handleUpgradeModal}>
            Upgrade Now
          </Button>
        </div>
      </div>
    </div>
  )
}

const Publishing = ({ type }) => {
  const { appId } = useParams()

  const { hasPublishedApps } = useFlags()

  const app = useSelector(state => getApp(state, appId))
  const domains = useSelector(state => getDomains(state, app?.Organization?.id))
  const needsUpgrade = !useSelector(state => !!getUpgraded(state, appId))

  const getAppHost = useCallback(
    domainId => {
      // assign domainId if one is not provided
      if (!domainId) domainId = app?.DomainId

      const domain = domains?.filter(d => d.id === domainId)[0]

      if (domain) {
        return `${domain?.domain}/`
      } else {
        const baseURL = process.env.REACT_APP_WEB_BASE_URL

        return `${app?.Organization?.subdomain}.${baseURL}/`
      }
    },
    [app?.id, domains?.length]
  )

  if (type !== 'web' && needsUpgrade && !hasPublishedApps) return <Upgrade />

  switch (type) {
    case 'android':
      return <AndroidPublish appId={appId} />
    case 'ios':
      return <IOSPublish appId={appId} />
    case 'pwa':
      return <PWAPublish appId={appId} getAppHost={getAppHost} />
    case 'web':
      return (
        <WebPublish
          appId={appId}
          getAppHost={getAppHost}
          needsUpgrade={needsUpgrade}
        />
      )
    default:
      return null
  }
}
